import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useSettings, cmsMediaUrl } from "../lib/cms";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/aanbod", label: "Aanbod" },
  { to: "/services", label: "Services" },
  { to: "/over-ons", label: "Over ons" },
  { to: "/verhalen", label: "Verhalen" },
  { to: "/events", label: "Events" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const companyName = settings?.company_name || "Maison Vellore";
  const tagline = settings?.tagline || "Curated Automobilia";
  const phone = settings?.phone || "+31 20 123 45 67";
  const phoneTel = phone.replace(/\s/g, "");
  const ctaText = settings?.default_cta_text || "Direct contact";
  const ctaLink = settings?.default_cta_link || "/contact";
  const logoUrl = cmsMediaUrl(settings?.logo);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-ink-950/85 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="h-9 w-auto"
            />
          ) : (
            <>
              <div className="relative w-8 h-8 flex items-center justify-center">
                <div className="absolute inset-0 border border-champagne/50 group-hover:border-champagne transition-colors duration-500" />
                <span className="font-display text-champagne text-lg leading-none">V</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display text-[22px] text-white tracking-tight">
                  {companyName}
                </span>
                <span className="text-[10px] tracking-widest-uc uppercase text-zinc-500 mt-0.5">
                  {tagline}
                </span>
              </div>
            </>
          )}
        </Link>

        <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                  isActive ? "text-champagne" : "text-zinc-300 hover:text-white"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${phoneTel}`}
            data-testid="header-phone"
            className="flex items-center gap-2 text-[13px] tracking-wider text-zinc-300 hover:text-champagne transition-colors duration-300 group"
          >
            <Phone size={14} className="text-champagne group-hover:scale-110 transition-transform" />
            <span className="font-body">{phone}</span>
          </a>
          <span className="h-8 w-px bg-white/10" />
          <Link
            to={ctaLink}
            data-testid="header-direct-contact"
            className="text-[12px] tracking-[0.22em] uppercase px-5 py-2.5 border border-white/15 text-white hover:border-champagne hover:text-champagne transition-colors duration-500"
          >
            {ctaText}
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Menu sluiten" : "Menu openen"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/10 hover:border-champagne/50 transition-colors"
        >
          {open ? <X size={18} className="text-white" /> : <Menu size={18} className="text-white" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden bg-ink-950/95 backdrop-blur-2xl border-t border-white/[0.06]"
        >
          <nav className="max-w-[1440px] mx-auto px-6 py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-4 text-sm tracking-[0.2em] uppercase hairline-b last:border-b-0 transition-colors ${
                    isActive ? "text-champagne" : "text-zinc-200 hover:text-white"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-3 pt-6 mt-2 border-t border-white/[0.06]">
              <a
                href={`tel:${phoneTel}`}
                data-testid="mobile-phone"
                className="flex items-center gap-2 text-sm text-zinc-200"
              >
                <Phone size={14} className="text-champagne" />
                <span>{phone}</span>
              </a>
              <Link
                to={ctaLink}
                data-testid="mobile-direct-contact"
                className="mt-2 inline-block text-center text-xs tracking-[0.22em] uppercase px-5 py-3 border border-champagne/50 text-champagne hover:bg-champagne hover:text-ink-950 transition-colors"
              >
                {ctaText}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
